import { Component, OnInit, OnDestroy, inject, ViewChild, AfterViewInit, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, switchMap, catchError } from 'rxjs/operators';
import { TreeSelectModule } from 'primeng/treeselect';
import { ButtonModule } from 'primeng/button';
import { AiFieldBoundaryService } from '@6grain/masofa-monolith-api-client/ai-inference/api/aiFieldBoundary.service';
import { OpenStreetMapComponent } from '@app/components/openstreetmap/openstreetmap.component';
import { MapControllerInput, MapInitOptions } from '@app/components/openstreetmap/interfaces';
import { RegionManagerService } from '@app/components/openstreetmap/managers/region-manager.service';
import { BackButton } from '@components/ui/back-button/back-button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ServerTranslateService } from '@services/server-translate.service';
import { FieldBoundaryModalComponent } from '../field-boundary-modal/field-boundary-modal.component';
import { environment } from '@root/src/environments/environment';
import { FormBuilder, IMsfFormBuilder } from '@components/form-builder/form-builder';
import { FieldFilter } from '@6grain/masofa-monolith-api-client/dictionaries';

declare var L: any;

@Component({
  selector: 'app-fields-identification-map',
  standalone: true,
  imports: [
    CommonModule,
    OpenStreetMapComponent,
    TreeSelectModule,
    FormsModule,
    BackButton,
    TranslateModule,
    ButtonModule,
    FieldBoundaryModalComponent,
    FormBuilder,
  ],
  templateUrl: './fields-identification-map.component.html',
  styleUrls: ['./fields-identification-map.component.scss'],
})
export class FieldsIdentificationMapComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly aiFieldService = inject(AiFieldBoundaryService);
  private readonly regionManager = inject(RegionManagerService);
  private readonly translate = inject(TranslateService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly ngZone = inject(NgZone);

  @ViewChild('mainMap') osmComponent!: OpenStreetMapComponent;
  aiBoundariesCount = 0;
  existingFieldsCount = 0;
  stopListCount = 0;
  isLoading = false;

  showAiFields: boolean = true;
  showExistingFields: boolean = true;
  showStopList: boolean = true;

  aiColor: string = '#26bd6c';
  existingColor: string = '#3388ff';
  stopListColor: string = '#ff4d4d';

  regionNodes: any[] = [];
  private rawRegionNodes: any[] = [];
  selectedRegion: any = null;
  private destroy$ = new Subject<void>();
  private mapMove$ = new Subject<void>();

  private aiLayerGroup: any = null;
  private fieldsLayerGroup: any = null;
  private stopListLayerGroup: any = null;
  private clusterLayerGroup: any = null;

  private lastData: { existingFields: any[]; aiBoundaries: any[]; stopListFields: any[]; clusters: any[] } | null =
    null;

  osmMapController = new Subject<MapControllerInput>();

  displayModal: boolean = false;
  selectedAiField: any = null;

  osmMapOptions: MapInitOptions = {
    center: [environment.map.lat, environment.map.lng],
    zoom: 12,
    height: '100%',
    showControls: true,
    baseMap: 'osm',
    disableBounds: true,
    showRegionNames: true,
    showDrawingControls: true,
    drawingMode: false,
  };
  tempPolygonCoordinates: [number, number][] | null = null;
  dateFilterModel: { createAt: Date[] | null } = { createAt: null };
  dateFilterFields: IMsfFormBuilder[] = [
    {
      name: 'createAt',
      title: 'Date',
      type: 'date',
      placeholder: 'Select period',
      options: {
        date: {
          selectionMode: 'range',
        },
      },
    },
  ];

  private checkIntervalId: any;
  private readonly onMapAction = () => this.mapMove$.next();

  ngOnInit(): void {
    this.setupMapStream();
    this.loadRegionTree();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.rebuildRegionNodes();
    });
  }

  ngAfterViewInit(): void {
    this.waitForMapInstance();
  }

  ngOnDestroy(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
    }

    const mapInstance = this.osmComponent?.getMapInstance();
    if (mapInstance) {
      mapInstance.off('moveend', this.onMapAction);
      mapInstance.off('zoomend', this.onMapAction);
      this.clearLayers();
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.mapMove$.complete();
    this.osmMapController.complete();
  }

  private async loadRegionTree() {
    try {
      this.rawRegionNodes = await this.regionManager.getRegionTree();
      this.rebuildRegionNodes();
    } catch (e) {
      console.error('Failed to load region tree', e);
    }
  }

  private rebuildRegionNodes(): void {
    const selectedRegionId = this.selectedRegion?.key || this.selectedRegion?.data?.id || this.selectedRegion?.id || null;
    this.regionNodes = this.mapToPrimeNgTree(this.rawRegionNodes);

    if (selectedRegionId) {
      this.selectedRegion = this.findRegionNodeByKey(this.regionNodes, selectedRegionId);
    }
  }

  private mapToPrimeNgTree(nodes: any[]): any[] {
    return nodes.map((node) => ({
      label: (this.serverTranslate.translate(node, 'names') as string) || node.name,
      data: node,
      key: node.id,
      children: node.children ? this.mapToPrimeNgTree(node.children) : [],
    }));
  }

  private findRegionNodeByKey(nodes: any[], key: string): any | null {
    for (const node of nodes) {
            
      if (node.key === key) {
        return node;
      }

      if (node.children?.length) {
        const foundNode = this.findRegionNodeByKey(node.children, key);
        if (foundNode) {
          return foundNode;
        }
      }
    }

    return null;
  }

  onApplyRegion(): void {
    const layersController = this.osmComponent?.getLayersController();
    const mapInstance = this.osmComponent?.getMapInstance();
    if (!layersController) {
      return;
    }

    if (this.selectedRegion) {
      const regionData = this.selectedRegion.data || this.selectedRegion;
      this.regionManager.filterByRegion(regionData, layersController);

      if (mapInstance) {
        if (regionData.lat && regionData.lon) {
          mapInstance.flyTo([regionData.lat, regionData.lon], 14);
        } else {
          setTimeout(() => {
            mapInstance.setZoom(14);
          }, 300);
        }
      }
    }

    // Always refresh data on apply (date-only filter should work too).
    this.mapMove$.next();
  }

  onLayerChange(): void {
    if (this.lastData) {
      this.drawLayers(
        this.lastData.existingFields,
        this.lastData.aiBoundaries,
        this.lastData.stopListFields,
        this.lastData.clusters,
      );
    }
  }

  private setupMapStream(): void {
    this.mapMove$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        switchMap(() => {
          const mapInstance = this.osmComponent?.getMapInstance();
          if (!mapInstance) return of(null);

          const zoom = mapInstance.getZoom();

          if (zoom < 9) {
            this.clearLayers();
            this.aiBoundariesCount = 0;
            this.existingFieldsCount = 0;
            this.stopListCount = 0;
            this.isLoading = false;
            this.lastData = null;
            return of(null);
          }

          this.isLoading = true;
          const bounds = mapInstance.getBounds();

          const requestBody = {
            minLon: bounds.getWest(),
            minLat: bounds.getSouth(),
            maxLon: bounds.getEast(),
            maxLat: bounds.getNorth(),
            zoom: zoom,
            query: {
              filters: this.buildCreateAtFilters(),
            },
          };

          return this.aiFieldService.aiInferenceAiFieldBoundaryGetBoundariesByBboxPost(requestBody).pipe(
            catchError((err) => {
              console.error('Error loading boundaries:', err);
              this.isLoading = false;
              return of(null);
            }),
          );
        }),
      )
      .subscribe((data: any) => {
        this.isLoading = false;
        if (data) {
          const aiBoundaries = data.aiBoundaries || [];
          const existingFields = data.existingFields || [];
          const stopListFields = data.stopListFields || [];
          const clusters = data.clusters || [];

          this.aiBoundariesCount = aiBoundaries.length;
          this.existingFieldsCount = existingFields.length;
          this.stopListCount = stopListFields.length;

          this.lastData = { existingFields, aiBoundaries, stopListFields, clusters };

          this.drawLayers(existingFields, aiBoundaries, stopListFields, clusters);
        }
      });
  }

  private waitForMapInstance(): void {
    this.checkIntervalId = setInterval(() => {
      const mapInstance = this.osmComponent?.getMapInstance();
      if (mapInstance) {
        clearInterval(this.checkIntervalId);
        this.loadRegions();

        mapInstance.on('moveend', this.onMapAction);
        mapInstance.on('zoomend', this.onMapAction);
        this.mapMove$.next();
      }
    }, 200);
  }

  private loadRegions(): void {
    this.regionManager
      .toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (polygons) => {
          if (polygons && polygons.length > 0) {
            this.regionManager.configurePolygonEvents(polygons, this.osmMapOptions);
            this.osmMapController.next({ addGisPolygons: polygons });
            setTimeout(() => {
              this.osmMapController.next({ setShowRegionNames: true });
            }, 100);
          }
        },
        error: (err) => {
          console.error('Error loading regions:', err);
        },
      });
  }

  private clearLayers(): void {
    const mapInstance = this.osmComponent?.getMapInstance();
    if (mapInstance) {
      if (this.fieldsLayerGroup) {
        mapInstance.removeLayer(this.fieldsLayerGroup);
        this.fieldsLayerGroup = null;
      }
      if (this.aiLayerGroup) {
        mapInstance.removeLayer(this.aiLayerGroup);
        this.aiLayerGroup = null;
      }
      if (this.stopListLayerGroup) {
        mapInstance.removeLayer(this.stopListLayerGroup);
        this.stopListLayerGroup = null;
      }
      if (this.clusterLayerGroup) {
        mapInstance.removeLayer(this.clusterLayerGroup);
        this.clusterLayerGroup = null;
      }
    }
  }

  private drawLayers(existingFields: any[], aiBoundaries: any[], stopListFields: any[], clusters: any[]): void {
    const mapInstance = this.osmComponent?.getMapInstance();
    if (!mapInstance || typeof L === 'undefined') return;

    this.clearLayers();

    const customPaneName = 'aiFieldsPane';
    if (!mapInstance.getPane(customPaneName)) {
      const pane = mapInstance.createPane(customPaneName);
      pane.style.zIndex = '1200';
      pane.style.pointerEvents = 'auto';
    }

    if (clusters && clusters.length > 0) {
      const currentLang = this.translate.currentLang || this.translate.defaultLang || 'ru-RU';

      const markers = clusters.map((c: any) => {
        let displayName = this.translate.instant('REGION');

        if (c.localizedNames) {
          if (c.localizedNames[currentLang]) {
            displayName = c.localizedNames[currentLang];
          } else if (c.localizedNames['ru-RU']) {
            displayName = c.localizedNames['ru-RU'];
          } else if (c.localizedNames['en-US']) {
            displayName = c.localizedNames['en-US'];
          } else {
            const keys = Object.keys(c.localizedNames);
            if (keys.length > 0) {
              displayName = c.localizedNames[keys[0]];
            }
          }
        }

        const size = c.count > 100 ? 50 : c.count > 10 ? 40 : 30;

        const html = `
          <div class="region-cluster-wrapper">
            <div class="cluster-marker" style="width:${size}px; height:${size}px; line-height:${size}px;">
              ${c.count}
            </div>
            <div class="cluster-label">${displayName}</div>
          </div>
        `;
        const icon = L.divIcon({
          html: html,
          className: 'custom-region-cluster',
          iconSize: [size, size + 24],
          iconAnchor: [size / 2, size / 2],
        });
        return L.marker([c.lat, c.lon], { icon: icon });
      });

      this.clusterLayerGroup = L.layerGroup(markers);
      this.clusterLayerGroup.addTo(mapInstance);
      return;
    }

    const tField = this.translate.instant('Field');
    const tArea = this.translate.instant('Area');
    const tUnnamed = this.translate.instant('UNNAMED');
    const tHa = this.translate.instant('HA');
    const tStopList = this.translate.instant('STOP_LIST');
    const tComment = this.translate.instant('COMMENT');

    const stopListFeatures = stopListFields
      .filter((f) => f.polygon)
      .map((f) => ({
        type: 'Feature',
        geometry: f.polygon,
        properties: {
          id: f.id,
          comment: f.comment,
        },
      }));

    if (this.showStopList && stopListFeatures.length > 0) {
      this.stopListLayerGroup = L.geoJSON(stopListFeatures, {
        pane: customPaneName,
        style: {
          color: this.stopListColor,
          weight: 2,
          fillColor: this.stopListColor,
          fillOpacity: 0.35,
          dashArray: '5, 5',
        },
        onEachFeature: (feature: any, layer: any) => {
          const p = feature.properties;
          const popupContent = `
                <div style="font-family: Inter, sans-serif;">
                    <strong>${tStopList}</strong><br/>
                    ${p.comment ? `<strong>${tComment}:</strong> ${p.comment}` : ''}
                </div>
                `;
          layer.bindPopup(popupContent);
          layer.on('mouseover', () => layer.bringToFront());
        },
      });
      this.stopListLayerGroup.addTo(mapInstance);
    }

    const fieldFeatures = existingFields
      .filter((f) => f.polygon)
      .map((f) => ({
        type: 'Feature',
        geometry: f.polygon,
        properties: {
          id: f.id,
          name: f.name,
          area: f.area,
        },
      }));

    if (this.showExistingFields && fieldFeatures.length > 0) {
      this.fieldsLayerGroup = L.geoJSON(fieldFeatures, {
        pane: customPaneName,
        style: {
          color: this.existingColor,
          weight: 2,
          fillColor: this.existingColor,
          fillOpacity: 0.2,
        },
        onEachFeature: (feature: any, layer: any) => {
          const p = feature.properties;
          const popupContent = `
          <div style="font-family: Inter, sans-serif;">
            <strong>${tField}:</strong> ${p.name || tUnnamed}<br/>
            <strong>${tArea}:</strong> ${p.area ? p.area.toFixed(2) : '-'} ${tHa}
          </div>
        `;
          layer.bindPopup(popupContent);
          layer.on('mouseover', () => layer.bringToFront());
        },
      });
      this.fieldsLayerGroup.addTo(mapInstance);
    }

    const aiFeatures = aiBoundaries
      .filter((b) => b.polygon)
      .map((b) => ({
        type: 'Feature',
        geometry: b.polygon,
        properties: {
          id: b.id,
          cadastralNumber: b.cadastralNumber,
          area: b.areaHa,
          perimeter: b.perimeterM,
          date: b.createAt,
          regionId: b.regionId,
        },
      }));

    if (this.showAiFields && aiFeatures.length > 0) {
      this.aiLayerGroup = L.geoJSON(aiFeatures, {
        pane: customPaneName,
        style: {
          color: this.aiColor,
          weight: 2,
          fillColor: this.aiColor,
          fillOpacity: 0.4,
          dashArray: '4, 4',
        },
        onEachFeature: (feature: any, layer: any) => {
          layer.on('click', () => {
            this.ngZone.run(() => {
              this.openFieldModal(feature.properties, feature.geometry);
            });
          });

          layer.on('mouseover', () => {
            layer.setStyle({ weight: 4, fillOpacity: 0.6 });
            layer.bringToFront();
          });

          layer.on('mouseout', () => {
            this.aiLayerGroup.resetStyle(layer);
          });
        },
      });
      this.aiLayerGroup.addTo(mapInstance);
    }
  }

  openFieldModal(properties: any, geometry: any): void {
    this.selectedAiField = { ...properties, polygon: geometry };
    this.displayModal = true;
  }

  onActionCompleted(): void {
    this.displayModal = false;
    this.selectedAiField = null;
    this.mapMove$.next();
  }

  onPolygonCreated(event: any): void {
    if (event.success && event.shape) {
      console.log('Полигон нарисован (ожидание подтверждения). Координаты:', event.shape.coordinates);
      this.tempPolygonCoordinates = event.shape.coordinates;
    } else {
      console.warn('Ошибка при рисовании:', event.error);
    }
  }

  onConfirmPolygon(): void {
    if (this.tempPolygonCoordinates) {
      this.processNewPolygonCoordinates(this.tempPolygonCoordinates);
      this.resetPolygonState();
      console.log('Координаты успешно подтверждены и переданы.');
    }
  }

  onResetPolygon(): void {
    this.resetPolygonState();
    console.log('Рисование отменено.');
  }

  private resetPolygonState(): void {
    this.tempPolygonCoordinates = null;
    this.osmMapController.next({ clearDrawings: true } as any);
  }

  private processNewPolygonCoordinates(coordinates: [number, number][]): void {
    console.log('Отправка координат в обработку...', coordinates);
  }

  onDateFilterModelChange(): void {
    this.mapMove$.next();
  }

  get canApplyFilters(): boolean {
    if (this.selectedRegion) {
      return true;
    }
    const range = this.dateFilterModel?.createAt;
    return Array.isArray(range) && !!(range[0] || range[1]);
  }

  private buildCreateAtFilters(): FieldFilter[] {
    const dateRange = this.dateFilterModel?.createAt;
    if (!Array.isArray(dateRange) || dateRange.length === 0) {
      return [];
    }

    const filters: FieldFilter[] = [];
    const [start, end] = dateRange;

    if (start) {
      filters.push({
        filterField: 'createAt',
        filterValue: new Date(start).toISOString(),
        filterOperator: 3, // >=
      });
    }

    if (end) {
      const endDate = new Date(end);
      endDate.setHours(23, 59, 59, 999);
      filters.push({
        filterField: 'createAt',
        filterValue: endDate.toISOString(),
        filterOperator: 5, // <=
      });
    }

    return filters;
  }
}
